import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { first, map } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import * as CoreActions from '@core/core.actions';
import { AuthorizeService } from '@platform/auth/authorize.service';
import * as PlatformActions from '@platform/platform.actions';
import { selectUser } from '@platform/platform.selectors';
import { CurrentUser } from '@platform/platform.state';
import { RouteGuard } from '@platform/routing/route.guard';
import { firstValueFrom } from 'rxjs';
import { ResourceStatuses } from '@platform/shared/resource.state';

@Injectable({
  providedIn: 'root',
})
export class CockpitGuard implements RouteGuard {
  constructor(
    private store$: Store<RootState>,
    private authorizeService: AuthorizeService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const openNotifications = route.queryParamMap.get('openNotifications');
    if (openNotifications) {
      this.openNotifications();
    }
    return await this.preloadUser();
  }

  async canActivateChild(childRoute: ActivatedRouteSnapshot): Promise<boolean> {
    const openNotifications = childRoute.parent!.queryParamMap.get('openNotifications');
    if (openNotifications) {
      this.openNotifications();
    }
    return await this.preloadUser();
  }

  private openNotifications() {
    this.store$.dispatch(CoreActions.showNotifications({ show: true }));
  }

  private async preloadUser(): Promise<boolean> {
    await this.getUser();

    return true;
  }

  private async getUser(): Promise<CurrentUser> {
    this.store$.dispatch(PlatformActions.loadCurrentUser());
    this.store$.dispatch(CoreActions.loadCustomerSpaceCount());

    return await firstValueFrom(
      this.store$.pipe(
        select(selectUser),
        first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
        map((state) => state.data),
      ),
    );
  }
}
